import type { Key } from "@react-types/shared";
import type { ToolbarButtonsItem } from "./types";

export interface OverflowMenuRoute {
  itemKey: Key;
  menuItemKey: Key;
}

export interface OverflowEntry {
  id: string;
  label?: string;
  isDisabled?: boolean;
}

/** Separator for composite overflow-menu keys of flattened menu-type items. */
const OVERFLOW_MENU_KEY_JOINER = "::";

/**
 * Builds the overflow menu's rows (and the lookup that routes their
 * selections) from the items that did not fit.
 *
 * Extracted from the component because the collapse that produces
 * `collapsedItems` is width-based and therefore unreachable in jsdom — the
 * mapping below would otherwise have no executable coverage at all.
 *
 * Menu-type items are FLATTENED (the wds Menu has no submenus): each of their
 * entries becomes a row under a synthetic key that routes through the map, so
 * item ids are never string-parsed and no character is reserved in the public
 * id contract.
 */
export function buildOverflowEntries(collapsedItems: ToolbarButtonsItem[]): {
  overflowEntries: OverflowEntry[];
  overflowRoutes: Map<string, OverflowMenuRoute>;
} {
  const overflowRoutes = new Map<string, OverflowMenuRoute>();
  const overflowEntries = collapsedItems.flatMap((item): OverflowEntry[] => {
    if (item.menuItems && item.menuItems.length > 0) {
      return item.menuItems.map((menuItem): OverflowEntry => {
        const syntheticKey = `${item.id}${OVERFLOW_MENU_KEY_JOINER}${menuItem.id}`;

        overflowRoutes.set(syntheticKey, {
          itemKey: item.id,
          menuItemKey: menuItem.id,
        });

        return {
          id: syntheticKey,
          label: `${item.label} — ${menuItem.label}`,
          isDisabled: menuItem.isDisabled,
        };
      });
    }

    return [
      {
        id: String(item.id),
        // The overflow menu is text-only, so an ICON-ONLY item has to fall
        // back to its accessible name or it collapses into a blank row that
        // no one — sighted or not — can identify.
        label: item.label ?? item["aria-label"],
        isDisabled: undefined,
      },
    ];
  });

  return { overflowEntries, overflowRoutes };
}

/**
 * Routes an overflow-menu selection. Entries flattened from a menu-type item
 * are found in the lookup built at render time; everything else is a plain
 * item and dispatches to onAction with its key VERBATIM — ids are never
 * string-parsed, so no character is reserved in the public item-id contract.
 */
export function routeOverflowMenuAction(
  key: Key,
  lookup: ReadonlyMap<string, OverflowMenuRoute>,
  onAction?: (key: Key) => void,
  onMenuItemAction?: (itemKey: Key, menuItemKey: Key) => void,
): void {
  const route = lookup.get(String(key));

  if (route) {
    onMenuItemAction?.(route.itemKey, route.menuItemKey);
  } else {
    onAction?.(key);
  }
}
